import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from 'primereact/button'
import { Chip } from 'primereact/chip'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Message } from 'primereact/message'
import { Fragment, useEffect, useId, useState } from 'react'
import { Controller, Form, useForm } from 'react-hook-form'
import z from 'zod'
import { useDepartmentsList } from '~/hooks/departmentsApi'
import useToast from '~/hooks/toast'
import {
  userSchema,
  userSchemaSubmission,
  useSyncUserMutation,
  useUsersList,
} from '~/hooks/usersApi'
import { UserRole } from '~/types/user.types'

type rowType = NonNullable<ReturnType<typeof useUsersList>['data']>[0]

const UserModal = ({ user, hide }: { user: -1 | false | rowType; hide: () => void }) => {
  const emailId = useId()
  const nameId = useId()
  const dropdownId = useId()
  const departmentsList = useDepartmentsList()
  const mutateUser = useSyncUserMutation()
  const toast = useToast()
  const form = useForm<z.infer<typeof userSchema>>({
    resolver: zodResolver(userSchema),
  })
  const selectedDepartments = form.getValues('departments') ?? []
  const setSelectedDepartmentRow = (i: number, d: (typeof selectedDepartments)[0]) => {
    const newValue = [...(form.getValues('departments') ?? [])]
    newValue.splice(i, 1, d)
    form.setValue('departments', newValue)
  }
  const removeDepartmentRow = (i: number) => {
    const newValue = [...(form.getValues('departments') ?? [])]
    newValue.splice(i, 1)
    form.setValue('departments', newValue)
  }
  useEffect(() => {
    if (typeof user !== 'object') return
    form.setValue('departments', user.departments)
    form.setValue('email', user.email)
    form.setValue('name', user.name)
    form.setValue('role', user.role)
  }, [user, form.setValue])
  form.watch()
  const onSubmit = () => {
    const validationResult = userSchemaSubmission.safeParse(form.getValues())
    if (validationResult.error) {
      const err = z.flattenError(validationResult.error)
      if (err.fieldErrors.departments) {
        form.setError('departments', { message: 'The department definitions is not unique' })
      }
      return
    }
    const departmentIds = (validationResult.data.departments ?? []).map((d) => d.departmentId)
    const uniqueDeptIds = [...new Set(departmentIds)]
    if (departmentIds.length > uniqueDeptIds.length) {
      form.setError('departments', { message: 'Cannot set the same department more than once' })
      return
    }
    mutateUser.mutate(validationResult.data, {
      onSuccess: () => {
        toast?.current?.show({
          severity: 'success',
          summary: user === -1 ? 'User Created' : 'User Updated',
          detail:
            user === -1
              ? `Welcome email sent to ${validationResult.data.email}`
              : `User ${validationResult.data.name} updated successfully`,
          life: 5000,
        })
        form.reset()
        hide()
      },
      onError: (error: unknown) => {
        toast?.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: error instanceof Error ? error.message : 'Failed to sync user',
          life: 5000,
        })
      },
    })
  }
  return (
    <Dialog
      visible={user !== false}
      header={user !== -1 && user !== false ? `Editing ${user.name}` : 'Create New User'}
      onHide={() => {
        hide()
        form.reset()
      }}
      className='min-w-120'
    >
      <Form control={form.control}>
        <div className='field my-4 flex flex-col'>
          <label htmlFor={emailId} className='text-sm font-bold'>
            Email
          </label>
          {user !== -1 && user ? (
            <p className='font-bold text-lg'>{user.email}</p>
          ) : (
            <>
              <InputText
                id={emailId}
                {...form.control.register('email')}
                className={form.formState.errors.email ? 'p-invalid' : ''}
              />
              {form.formState.errors.email && (
                <Message
                  severity='error'
                  text={form.formState.errors.email.message}
                  className='mt-2'
                />
              )}
            </>
          )}
        </div>
        <div className='field my-4 flex flex-col'>
          <label htmlFor={nameId} className='text-sm font-bold'>
            Name
          </label>
          <InputText
            id={nameId}
            {...form.control.register('name')}
            className={`h-9 px-4 pb-1 pt-2 ${form.formState.errors.name ? 'p-invalid' : ''}`}
          />
          {form.formState.errors.name && (
            <Message severity='error' text={form.formState.errors.name.message} className='mt-2' />
          )}
        </div>
        <div className='field my-4 flex flex-col'>
          <label htmlFor={nameId} className='text-sm font-bold'>
            Role
          </label>
          <Controller
            control={form.control}
            name='role'
            render={({ field }) => (
              <Dropdown
                {...field}
                pt={{ input: { className: 'h-9 px-4 pb-1 pt-2' } }}
                id={dropdownId}
                className={form.formState.errors.role ? 'w-full p-invalid' : 'w-full '}
                options={[
                  { value: UserRole.Viewer, label: 'Viewer' },
                  { value: UserRole.Contributor, label: 'Contributor' },
                  { value: UserRole.Admin, label: 'Admin' },
                  { value: UserRole.Officiator, label: 'Officiator' },
                ]}
                disabled={field.value === UserRole.Officiator}
                optionDisabled={(d) => d.value === UserRole.Officiator}
                onChange={(e) => field.onChange(e.value)}
              />
            )}
          />
          {form.formState.errors.role && (
            <Message severity='error' text={form.formState.errors.role.message} className='mt-2' />
          )}
        </div>
        <div className='field my-4 flex flex-col'>
          <div className='flex justify-between items-baseline'>
            <label htmlFor={nameId} className='text-sm font-bold'>
              Departments
            </label>
            <div>
              <Button
                link
                icon='pi pi-plus'
                className='py-1'
                size='small'
                disabled={(departmentsList.data?.length ?? 0) === (selectedDepartments.length ?? 0)}
                onClick={() =>
                  form.setValue('departments', [
                    ...selectedDepartments,
                    { departmentId: undefined, role: undefined },
                  ])
                }
              />
            </div>
          </div>
          <div className='grid grid-cols-8 gap-1'>
            {selectedDepartments.map((d, i) => {
              return (
                <Fragment
                  key={
                    // biome-ignore lint/suspicious/noArrayIndexKey: probably no other solution
                    i
                  }
                >
                  <Dropdown
                    pt={{ input: { className: 'h-9 px-4 pb-1 pt-2' } }}
                    optionDisabled={(o) =>
                      !!selectedDepartments.find((d) => d.departmentId === o.id)
                    }
                    options={departmentsList.data}
                    optionValue='id'
                    optionLabel='title'
                    className='col-span-4'
                    value={d.departmentId}
                    disabled={(d.departmentId ?? 0) > 0}
                    onChange={(e) => setSelectedDepartmentRow(i, { ...d, departmentId: e.value })}
                  />
                  <Dropdown
                    className='col-span-3'
                    pt={{ input: { className: 'h-9 px-4 pb-1 pt-2' } }}
                    options={[
                      { value: 1, label: 'Contributor' },
                      { value: 2, label: 'Admin' },
                    ]}
                    value={d.role === 0 ? undefined : d.role}
                    onChange={(e) => setSelectedDepartmentRow(i, { ...d, role: e.value })}
                  />
                  <Button
                    icon='pi pi-times'
                    size='small'
                    link
                    onClick={() => removeDepartmentRow(i)}
                  />
                </Fragment>
              )
            })}
          </div>
          {form.formState.errors.departments && (
            <Message
              severity='error'
              text={form.formState.errors.departments.message}
              className='mt-2'
            />
          )}
        </div>
        <div className='flex justify-end'>
          <Button
            icon='pi pi-save'
            type='submit'
            onClick={onSubmit}
            disabled={mutateUser.isLoading}
            label='Save'
            size='small'
          />
        </div>
      </Form>
    </Dialog>
  )
}
const renderDeptTags = (l: rowType) => {
  if (!l.departments) return 'No departments'
  return (
    <div className='flex gap-1'>
      {l.departments.map(({ departmentId, title }) => (
        <Chip className='text-md font-bold' label={title} key={departmentId} />
      ))}
    </div>
  )
}
const renderRoleCol = (l: rowType) => {
  switch (l.role) {
    case 1:
      return <Chip label='Viewer' className='bg-indigo-300 text-md font-bold' />
    case 2:
      return <Chip label='Contributor' className='bg-cyan-300 text-md font-bold' />
    case 3:
      return <Chip label='Admin' className='bg-lime-300 text-md font-bold' />
    case 4:
      return <Chip label='Officiator' className='bg-orange-300 text-md font-bold' />
  }
  return '(unknown)'
}

const UsersPage = () => {
  const userList = useUsersList()
  const [activeUser, setActiveUser] = useState<false | -1 | rowType>(false)
  return (
    <div>
      <UserModal user={activeUser} hide={() => setActiveUser(false)} />
      <div className='flex w-full justify-between mb-4 items-baseline'>
        <h1 className='text-3xl'>User List</h1>
        <div className='flex gap-1'>
          <Button icon='pi pi-refresh' size='small' link onClick={() => userList.refetch()} />
          <Button icon='pi pi-plus' size='small' link onClick={() => setActiveUser(-1)} />
        </div>
      </div>
      <DataTable
        value={userList.data ?? []}
        onRowClick={(r) => setActiveUser(r.data as rowType)}
        pt={{ bodyRow: { className: 'cursor-pointer' } }}
      >
        <Column key='name' field='name' header='Name' />
        <Column key='email' field='email' header='Email' />
        <Column key='role' field='role' header='Role' body={renderRoleCol} />
        <Column key='departments' header='Departments' body={renderDeptTags} />
      </DataTable>
    </div>
  )
}

export default UsersPage
